//Router: */user-profile
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ProductItem from "../components/ProductItem";
import { FollowProvider } from "../context/FollowContext";
import CuruChatsPost from "./CuruChatsPost";
import Icons from "./Icons";
import api from "./api";
import "./profile.css"; // Import the CSS file

const Profile = () => {
  const [profile, setProfile] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [reviewsToShow, setReviewsToShow] = useState(4);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(2);


  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };


  // Function to fetch profile data
  const fetchProfile = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setError("No token found. Please log in again.");
      setLoading(false);
      navigate("/");
      return;
    }

    try {
      const response = await fetch("https://dev-api.curu.com.au/api/profile", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok)
        throw new Error(`Failed to fetch profile data: ${response.statusText}`);
      const data = await response.json();
      setProfile(data);
    } catch (error) {
      setError("Failed to load profile. Please try again later.");
      console.error("Error fetching profile:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch user profile data
  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  useEffect(() => {
    const fetchProfileData = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("No token found.");
        return;
      }

      try {
        // Fetch both reviews and ratings
        const [reviewsResponse, ratingsResponse] = await Promise.all([
          fetch("https://dev-api.curu.com.au/api/profile/review", {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch("https://dev-api.curu.com.au/api/profile/rate", {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        if (!reviewsResponse.ok)
          throw new Error(
            `Failed to fetch reviews: ${reviewsResponse.statusText}`
          );
        if (!ratingsResponse.ok)
          throw new Error(
            `Failed to fetch ratings: ${ratingsResponse.statusText}`
          );

        const reviewsData = await reviewsResponse.json();
        const ratingsData = await ratingsResponse.json();

        // Merge reviews and ratings
        const mergedData = reviewsData.map((review) => {
          // Find corresponding rating by product ID
          const rating = ratingsData.find((r) => r.id === review.id);

          return {
            ...review,
            rate: rating ? rating.rate : review.rate || 0, // Use rating if available
            rate_count: rating ? rating.rate_count : review.rate_count || 0, // Use rate count if available
          };
        });

        // Calculate total review count (if necessary)
        const totalReviews = mergedData.reduce((count, review) => {
          return count + (review.user_reviews ? review.user_reviews.length : 0);
        }, 0);

        setProfile((prevProfile) => ({
          ...prevProfile,
          review_count: totalReviews, // Dynamically set review count
        }));

        setReviews(mergedData); // Set merged data
      } catch (error) {
        setError("Failed to load reviews and ratings. Please try again later.");
        console.error("Error fetching data:", error);
      }
    };
    fetchProfileData();
  }, []);

  useEffect(() => {
    const fetchPosts = async () => {
      if (!profile?.id) return;

      setIsLoading(true);
      try {
        const res = await api.get("/comunity/post");
        const filteredPosts = res.data.filter(
          (post) => post.user?.id === profile.id
        );
        setPosts(filteredPosts);
        //console.log("Filtered posts:", filteredPosts);
      } catch (error) {
        console.error("Error fetching posts:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPosts();
  }, [profile]); // Run this effect when `profile` is updated

  // Mapping reviews (now with merged ratings and reviews)
  const mappedReviews = reviews
    .filter(
      (review) =>
        review.rate || (review.user_reviews && review.user_reviews.length > 0)
    ) // Include items with either a rating or user reviews
    .map((review) => ({
      id: review.id,
      name: review.name,
      image: review.image,
      rate: parseFloat(review.rate) || 0,
      rate_count: review.rate_count || 0,
      brand: review.brand,
      category: review.category,
      sub_category: review.sub_category,
      user_reviews: review.user_reviews
        ? review.user_reviews.map((userReview) => ({
            id: userReview.id,
            text: userReview.text,
            rate: parseFloat(userReview.rate) || 0,
            created_ago: userReview.created_ago,
            like_count: userReview.like_count,
            is_liked: userReview.is_liked,
            author: userReview.author,
            photos: userReview.photos.map((photo) => ({
              id: photo.id,
              url: photo.photo,
            })),
          }))
        : [], // Ensure user_reviews is always an array, even if empty
    }));

  const openEditProfile = () => {
    navigate("/edit-profile"); // Navigate to the EditProfile page
  };

  // Handle navigation state (e.g., after update)
  useEffect(() => {
    if (location.state?.updated) {
      // Optionally, show a success message or perform other actions
      console.log("Profile was updated.");
      // Refresh the profile data to reflect updates
      fetchProfile();
      // Clear the state to prevent repeated actions
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, navigate]);

  // Edit handlers
  const handleEditSkinType = () =>
    navigate("/skin-type", { state: { fromProfile: true } });
  const handleEditSkinUndertone = () =>
    navigate("/select-undertone", { state: { fromProfile: true } });
  const handleEditSkinSensitivity = () =>
    navigate("/skin-sensitivity", { state: { fromProfile: true } });
  const handleEditSkinTone = () =>
    navigate("/select-skintone", { state: { fromProfile: true } });

  const handleProfileClick = (user) => {
    if (!user || !user.id) return;
    navigate(`/profile/${user.id}`, { state: { user } });
  };

  if (loading) return <p>Loading profile...</p>;
  if (error) return <p className="error-message">{error}</p>;

  return (
    <div className="profile-page">
      <div className="profile-header">
        <div className="profile-banner">
          <div className="profile-picture-container">
            {profile?.photo ? (
              <img
                src={profile.photo}
                alt={profile.name || "Profile"}
                className="profile-picture"
              />
            ) : (
              <div className="profile-picture">
                <Icons.Profile />
              </div>
            )}
            <div className="level-badge">LVL {profile.level?.id || "N/A"}</div>
          </div>

          <div className="profile-info-container">
            {/* Profile Info (Name and Username) */}
            <div className="profile-info">
              <h1>
                {profile.first_name} {profile.last_name || ""}
              </h1>
              <p>{profile.username}</p>
            </div>

            {/* Progress Bar and XP Info */}
            <div className="level-progress-container">
              {/* Progress Bar */}
              <div className="progress-bar-wrapper">
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{
                      width: `${
                        (profile.experience / profile.level?.max_experience) *
                        100
                      }%`,
                    }}
                  ></div>
                </div>

                {/* Tooltip (Question Mark Icon) */}
                <div className="custom-tooltip" onClick={toggleModal}>
                  <img
                    src={`/images/icons/xp-questionmark.svg`}
                    alt="xp Icon"
                    className="question-mark-icon"
                  />
                  <span className="custom-tooltip-text">
                    Additional info about leveling up
                  </span>
                </div>
              </div>
              {/* XP Info and Tooltip */}
              <div className="xp-info-container">
                {/* XP Info */}
                <div className="xp-info">
                  <p className="current-xp">
                    XP {profile.experience}/{profile.level?.max_experience}
                  </p>
                  <p className="xp-to-level-up">
                    {profile.level?.max_experience - profile.experience} XP to
                    level up
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div
            className={`modal ${isModalOpen ? "show" : ""}`}
            onClick={toggleModal}
          >
            <div
              className="modal-content profile-modal-content"
              onClick={(e) => e.stopPropagation()}
            >
              <h1>How to earn XP</h1>
              <p>
                Level up for being active on Curu. Psst, watch this space for
                surprises in the making.
              </p>

              <ul className="xp-tasks">
                <li className="xp-task">
                  <div className="quiz-icon">
                    <img
                      src={`/images/icons/questionmark.svg`}
                      alt="quiz Icon"
                      className="quiz-img"
                    />
                  </div>
                  <span>Complete skin profile quiz</span>
                  <span className="xp-points">+ 50 XP</span>
                </li>
                <li className="xp-task">
                  <div className="profile-picture-icon">
                    <img
                      src={`/images/icons/profile-pic-modal.svg`}
                      alt="Profile Icon"
                      className="profile-img"
                    />
                  </div>
                  <span>Add a profile picture</span>
                  <span className="xp-points">+ 20 XP</span>
                </li>
                <li className="xp-task">
                  <div className="tick-icon">
                    <img
                      src="/images/icons/tick.svg"
                      alt="Tick Icon"
                      className="tick-img"
                    />
                  </div>
                  <span>Check-in to the Curu app (daily)</span>
                  <span className="xp-points">+ 20 XP</span>
                </li>

                <li className="xp-task">
                  <div className="add-icon">
                    <img
                      src="/images/icons/add.svg"
                      alt="Add Icon"
                      className="add-img"
                    />
                  </div>
                  <span>Write a post in Curu Chats</span>
                  <span className="xp-points">+ 50 XP</span>
                </li>
                <li className="xp-task">
                  <div className="star-icon">
                    <img
                      src="/images/icons/profile-review-star.svg"
                      alt="Star Icon"
                      className="star-img"
                    />
                  </div>
                  <span>Write a product review</span>
                  <span className="xp-points">+ 30 XP</span>
                </li>
                <li className="xp-task">
                  <div className="photo-icon">
                    <img
                      src="/images/icons/photo.svg"
                      alt="Art Icon"
                      className="art-img"
                    />
                  </div>
                  <span>Add a photo review</span>
                  <span className="xp-points">+ 5 XP</span>
                </li>
                <li className="xp-task">
                  <div className="tag-icon">
                    <img
                      src="/images/icons/tag.svg"
                      alt="Tag Icon"
                      className="tag-img"
                    />
                  </div>
                  <span>Tag a product in Curu Chats</span>
                  <span className="xp-points">+ 5 XP</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="follower-info-container">
            <div className="follower-info">
              <div className="follower-item">
                <strong>{profile.followers_count || "0"}</strong>
                <span>Followers</span>
              </div>
              <div className="follower-item">
                <strong>{mappedReviews.length || "0"}</strong>
                <span>Reviews</span>
              </div>
            </div>
            <div>
              {/*Button to open the edit profile modal or functionality*/}
              <button className="edit-profile-button" onClick={openEditProfile}>
                Edit Profile
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Heading for the skin information section*/}
      <h2 className="skin-heading"> My Skin</h2>
      {/* Show banner when skin quiz is not taken */}
      {(!profile.skin_type || profile.skin_type.name === "Not set") && (
        <div className="skin-quiz-banner" style={{ width: "100%" }}>
          <p>
            {" "}
            You have not taken the skin quiz yet! Take the quiz to add your skin
            profile
          </p>
          <button
            className="take-quiz-button"
            onClick={() => navigate("/skin-type")}
          >
            Take Quiz
          </button>
        </div>
      )}
      {/* Only show grid if skin quiz has been taken */}
      {profile.skin_type && profile.skin_type.name !== "Not set" && (
        <div className="skin-info-grid">
          {profile && (
            <>
              {[
                "skin_type",
                "skin_concerns",
                "skin_undertone",
                "skin_sensitive_type",
                "skin_tone",
              ].map((key) => (
                <div className={`skin-box skin-box-${key}`} key={key}>
                  <div className="skin-label">
                    {key
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (char) => char.toUpperCase())}
                  </div>
                  <button
                    onClick={
                      key === "skin_type"
                        ? handleEditSkinType
                        : key === "skin_undertone"
                        ? handleEditSkinUndertone
                        : key === "skin_sensitive_type"
                        ? handleEditSkinSensitivity
                        : key === "skin_tone"
                        ? handleEditSkinTone
                        : () =>
                            navigate("/skin-concerns", {
                              state: { fromProfile: true },
                            })
                    }
                    className="edit-pencil"
                  >
                    <img
                      src="/images/icons/pencil.svg"
                      alt="Pencil Icon"
                      className="pencil-img"
                    />
                  </button>
                  <div className="skin-value-container">
                    <div className="skin-value">
                      {key === "skin_concerns" ? (
                        profile.skin_concerns &&
                        profile.skin_concerns.length > 0 ? (
                          <>
                            <span>{profile.skin_concerns[0].name}</span>
                            {profile.skin_concerns.length > 1 && (
                              <span> +{profile.skin_concerns.length - 1}</span>
                            )}
                          </>
                        ) : (
                          "None"
                        )
                      ) : (
                        profile[key]?.name || "Not set"
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}
      <div className="">
        <h2 className="reviews-heading">
          {" "}
          Reviews{" "}
          <span className="products-count">
            ({mappedReviews.length} products)
          </span>{" "}
        </h2>
        {mappedReviews.length === 0 ? (
          <p>You have not submitted any reviews yet.</p>
        ) : (
          <>
            <div className="reviews-container"></div>
            <div className="product-grid-view">
              <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-3 row-cols-xl-4 g-4 mt-3">
                {mappedReviews.slice(0, reviewsToShow).map((item) => (
                  <div key={item.id} className="col">
                    <ProductItem item={item} starsAtBottom={true} />
                  </div>
                ))}
              </div>
            </div>

            {/* "Load More" Button */}
            {reviewsToShow < mappedReviews.length && (
              <div className="load-more-container">
                <button
                  className="load-more-btn"
                  onClick={() => setReviewsToShow(reviewsToShow + 4)}
                >
                  Load More
                </button>
              </div>
            )}
          </>
        )}
      </div>
      <FollowProvider>
        <div className="posts-section">
          <div className="posts-heading">
            Posts
            {!isLoading && (
              <span className="products-count">
                (
                {posts.length === 0
                  ? "No posts"
                  : posts.length === 1
                  ? "1 post"
                  : `${posts.length} posts`}
                )
              </span>
            )}
          </div>
          <div className="posts-container">
            {isLoading ? (
              <p>Loading posts...</p>
            ) : posts && posts.length > 0 ? (
              <>
                {posts
                  .sort(
                    (a, b) => new Date(b.created_at) - new Date(a.created_at)
                  ) // Sort by created_at
                  .slice(0, visibleCount)
                  .map((post) => (
                    <CuruChatsPost
                      key={post.id}
                      post={post}
                      onProfileClick={handleProfileClick}
                      onDeleteSuccess={() =>
                        setPosts((prev) => prev.filter((p) => p.id !== post.id))
                      }
                    />
                  ))}
                {visibleCount < posts.length && (
                  <div className="text-center my-4">
                    <button
                      className="submit-button"
                      onClick={() => setVisibleCount((prev) => prev + 2)}
                    >
                      Load More
                    </button>
                  </div>
                )}
              </>
            ) : (
              <p>No posts found.</p>
            )}
          </div>
        </div>
      </FollowProvider>

    </div>
  );
};

export default Profile;
